import { useAppDispatch, useAppSelector } from "../../store";
import { updateScrewFilters, updatePropertyIdMappings } from "../../store/reducers/ScrewsReducers";
import {
  getScrewFilters,
  getScrewProperties,
  getScrewFetchStatus,
} from "../../store/selectors/ScrewSelectors";
import { useNavigate } from "react-router-dom";
import { setIsUploadStarted } from "../../store/reducers/DatasetReducer";
import { updateUploadedScrewData } from "../../store/reducers/ScrewsReducers";
import { useEffect, useMemo, useCallback } from "react";
import { 
  getScrewsPropertiesAction,
  createScrewPropertyAction,
  updateScrewPropertyAction,
  deleteScrewPropertyValueAction
} from "../../store/actions/ScrewsActions";
import { ScrewPropertiesResponse, ScrewProperty } from "../../types/ScrewTypes";
import NumericFilter from "./NumericFilter";
import SelectDropDown from "../SelectDropdown/SelectDropDown";
import { Modal, Box, Alert, Button, TextField } from "@mui/material";
import { useState } from "react";
import { DropdownCheck, DropdownClear } from "../../Icons/Icons";
import "./css/GridNav.scss";
import "./css/NumericFilter.scss";
const NUMERIC_PROPERTIES = ["total_length", "head_diameter"];

interface FilterOption {
  value: string;
  label: string;
}

interface FilterConfig {
  type: string;
  label: string;
  options: FilterOption[];
  selectedValue?: string;
  isNumeric: boolean;
  hasCreateNewFilter?: boolean;
  hasEditableFilters?: boolean;
}

const ScrewGridNavigation = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const screwFilters = useAppSelector(getScrewFilters);
  const screwProperties = useAppSelector(getScrewProperties) as ScrewPropertiesResponse | null;
  const screwFetchStatus = useAppSelector(getScrewFetchStatus);
  const isScrewDataFetching = screwFetchStatus === "loading";
  
  // Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalFilterValue, setModalFilterValue] = useState("");
  const [filterToDelete, setFilterToDelete] = useState("");
  const [filterToUpdate, setFilterToUpdate] = useState("");
  const [updatedFilterValue, setUpdatedFilterValue] = useState("");
  const [currentFilterType, setCurrentFilterType] = useState<string>("");

  useEffect(() => {
    if (!screwProperties && !isScrewDataFetching) {
      dispatch(getScrewsPropertiesAction());
    }
  }, [dispatch, isScrewDataFetching, screwProperties]);

  useEffect(() => {
    if (!screwProperties?.items) return;

    const mappings: { [propertyName: string]: { [value: string]: string } } = {};
    
    screwProperties.items.forEach((property: ScrewProperty) => {
      mappings[property.propertyname] = {};
      property.categoricalvalues.forEach((value: string) => {
        mappings[property.propertyname][value] = property.propertyid;
      });
    });

    dispatch(updatePropertyIdMappings(mappings));
  }, [screwProperties, dispatch]);

  const onFilterAdd = useCallback((propertyName: string, value: string) => {
    if (!screwProperties?.items) return;
    
    const property = screwProperties.items.find((p: ScrewProperty) => p.propertyname === propertyName);
    if (!property) return;

    if (NUMERIC_PROPERTIES.includes(propertyName)) {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) {
        dispatch(updateScrewFilters({ 
          ...screwFilters, 
          [property.propertyid]: undefined
        }));
      } else {
        const matchingValues = property.categoricalvalues
          .map((v: string) => parseFloat(v))
          .filter((v: number) => !isNaN(v) && isWithinRange(v, numericValue))
          .map((v: number) => v.toString());

        dispatch(updateScrewFilters({ 
          ...screwFilters, 
          [property.propertyid]: matchingValues.length > 0 ? matchingValues.join(",") : value
        }));
      }
    } else {
      dispatch(updateScrewFilters({ 
        ...screwFilters, 
        [property.propertyid]: value 
      }));
    }
    navigate("/dataset/screws/1");
  }, [dispatch, navigate, screwFilters, screwProperties]);

  const onFilterClear = useCallback((propertyName: string) => {
    if (!screwProperties?.items) return;
    
    const property = screwProperties.items.find((p: ScrewProperty) => p.propertyname === propertyName);
    if (!property) return;

    dispatch(
      updateScrewFilters({
        ...screwFilters,
        [property.propertyid]: undefined,
      })
    );
    navigate("/dataset/screws/1");
  }, [dispatch, navigate, screwFilters, screwProperties]);

  const onSelectScrewImage = useCallback(() => {
    dispatch(updateUploadedScrewData({ imagepath: "" }));
    dispatch(setIsUploadStarted(true));
  }, [dispatch]);

  const createTextFilter = useCallback((propertyName: string, value: string) => {
    const property = screwProperties?.items?.find((x: ScrewProperty) => x.propertyname === propertyName);
    if (!property) return;
    
    dispatch(createScrewPropertyAction({ propertyId: property.propertyid, propertyValue: value }));
  }, [dispatch, screwProperties]);

  const deleteFilter = useCallback((propertyName: string, value: string) => {
    const property = screwProperties?.items?.find((x: ScrewProperty) => x.propertyname === propertyName);
    if (!property) return;

    dispatch(deleteScrewPropertyValueAction({ propertyId: property.propertyid, value }));
    setDeleteModalOpen(false);
  }, [dispatch, screwProperties]);

  const editFilter = useCallback((propertyName: string, oldValue: string, newValue: string) => {
    const property = screwProperties?.items?.find((x: ScrewProperty) => x.propertyname === propertyName);
    if (!property) return;

    dispatch(updateScrewPropertyAction({ 
      propertyId: property.propertyid, 
      oldValue,
      newValue
    }));
    setEditModalOpen(false);
  }, [dispatch, screwProperties]);

  const isWithinRange = (value: number, target: number, tolerance: number = 0.1) => {
    const range = target * tolerance;
    return value >= target - range && value <= target + range;
  };
  
  // Modal handlers
  const handleModalOpen = (filterType: string, value: string) => {
    setCurrentFilterType(filterType);
    setModalFilterValue(value);
    setUpdatedFilterValue(value); // Initialize with current value
    setDeleteModalOpen(true);
  };
  
  const handleEditModalOpen = (filterType: string, value: string) => {
    setCurrentFilterType(filterType);
    setModalFilterValue(value);
    setFilterToUpdate(value);
    setUpdatedFilterValue(value); // Initialize with current value
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setDeleteModalOpen(false);
  };
  
  const handleEditModalClose = () => {
    setEditModalOpen(false);
  };

  const handleDeleteItem = () => {
    deleteFilter(currentFilterType, modalFilterValue);
  };

  const handleUpdateItem = () => {
    editFilter(currentFilterType, modalFilterValue, updatedFilterValue);
  };

  const filters = useMemo(() => {
    if (!screwProperties?.items) return [];

    const labelProperties = screwProperties.items.filter(property => property.islabel);
    const nonLabelProperties = screwProperties.items.filter(property => !property.islabel);

    const sortedNonLabelProperties = nonLabelProperties.sort((a, b) => {
      const aName = a.propertyname.toLowerCase();
      const bName = b.propertyname.toLowerCase();

      if (aName === 'brand') return -1;
      if (bName === 'brand') return 1;
      if (aName === 'platform') return -1;
      if (bName === 'platform') return 1;

      return aName.localeCompare(bName);
    });

    return [...labelProperties, ...sortedNonLabelProperties].map(property => {
      const values = property.categoricalvalues || [];
      const sortedValues = NUMERIC_PROPERTIES.includes(property.propertyname)
        ? [...values].sort((a, b) => parseFloat(a) - parseFloat(b))
        : [...values].sort((a, b) => {
            if (!a) return -1;
            if (!b) return 1;
            return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
          });

      return {
        type: property.propertyname,
        label: property.propertyname.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '),
        options: sortedValues.map(value => ({
          value: value,
          label: value
        })),
        selectedValue: screwFilters[property.propertyid],
        isNumeric: NUMERIC_PROPERTIES.includes(property.propertyname),
        hasCreateNewFilter: !NUMERIC_PROPERTIES.includes(property.propertyname),
        hasEditableFilters: !NUMERIC_PROPERTIES.includes(property.propertyname)
      };
    });
  }, [screwProperties, screwFilters]);

  return (
    <>
      <nav className="nav">
        {onSelectScrewImage && (
          <ul className="submenu">
            <li>
              <Button className="button button-blue" onClick={onSelectScrewImage}>
                Add +
              </Button>
            </li>
          </ul>
        )}
        <ul>
          {filters.map((filter) => (
            <li key={filter.type}>
              {filter.isNumeric ? (
                <NumericFilter
                  label={filter.label}
                  value={filter.selectedValue || ""}
                  onChange={(value) => onFilterAdd(filter.type, value)}
                  onClear={() => onFilterClear(filter.type)}
                  className={filter.selectedValue ? "active" : ""}
                />
              ) : (
                <SelectDropDown
                  options={[
                    { label: `Select ${filter.label.toLowerCase()}`, value: "undefined" },
                    ...filter.options,
                  ]}
                  startAdornment={<DropdownCheck />}
                  endAdornment={
                    <span onClick={() => onFilterClear(filter.type)}>
                      <DropdownClear />
                    </span>
                  }
                  selectedOption={filter.selectedValue || "undefined"}
                  onChange={(evt) => onFilterAdd(filter.type, evt.target.value)}
                  createFilter={(value) => createTextFilter(filter.type, value)}
                  deleteFilter={
                    filter.hasEditableFilters ? 
                    (_: string, value: string) => {
                      setFilterToDelete(value);
                      handleModalOpen(filter.type, value);
                    } : undefined
                  }
                  editFilter={
                    filter.hasEditableFilters ?
                    (_: string, value: string) => {
                      handleEditModalOpen(filter.type, value);
                    } : undefined
                  }
                  hasCreateNewFilter={filter.hasCreateNewFilter}
                  hasEditableFilters={filter.hasEditableFilters}
                  className={filter.selectedValue && filter.selectedValue !== "undefined" ? "active" : ""}
                />
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Delete Modal */}
      <Modal className="modal" open={deleteModalOpen} onClose={handleModalClose}>
        <Box className="delete-locator-property-modal-box">
          <Alert className="warning-modal" variant="filled" severity="warning">
            Warning: You may lose data!
          </Alert>
          <p>
            You are removing {currentFilterType} "{modalFilterValue}" from the {currentFilterType} labels. 
            All images with this label will lose their {currentFilterType} label.
          </p>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button onClick={handleDeleteItem}>Delete</Button>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal className="modal" open={editModalOpen} onClose={handleEditModalClose}>
        <Box className="delete-locator-property-modal-box">
          <Alert className="warning-modal" variant="filled" severity="warning">
            Warning: You might update data that is linked to multiple items!
          </Alert>
          <p>
            You are updating {currentFilterType}: "{modalFilterValue}" to:
          </p>
          <div className="new-filter-textfield-container">
            <TextField 
              id="new-filter-textfield" 
              onKeyDown={(e) => e.stopPropagation()} 
              value={updatedFilterValue} 
              onChange={(e) => setUpdatedFilterValue(e.target.value)} 
            />
          </div>
          <Button onClick={handleEditModalClose}>Cancel</Button>
          <Button onClick={handleUpdateItem}>Update</Button>
        </Box>
      </Modal>
    </>
  );
};

export default ScrewGridNavigation; 